/**
 * Ground weapons, the looks (BUILDLIST 65.5; decision 118: a rifle and a pistol, with magazines and
 * reload). The rifle is today's handheld — its model unchanged, now with a magazine that comes out
 * for the reload. The pistol is the sidearm: a short slide over a frame and grip, a team flash on
 * the slide, its magazine in the grip. Both built along +Z in person units (a person is 1.8), the
 * style sheet's rules (§10.2): one metal, one flash, panels not greebles; readable at Ground, gone at
 * Theater (the far silhouette keeps its one rifle block — a pistol is too small to matter there).
 *
 * (This module is the Ground rung's hand weapons; rendering/weaponModels.ts is the turn game's launchers.)
 *
 * Anchors: `muzzle` (a tracer starts here), `gripR` / `gripL` (where the hands go), `magazine` (the
 * group the reload animation pulls out and pushes back — rendering/personModel.ts), `well` (where it
 * seats). Lane C's mechanics (65.1–65.3) own the numbers; nothing here is read by the sim.
 */
import * as THREE from 'three';
import { markShared } from './meshBake';
import type { UnitPalette } from './unitPalette';

export type GroundWeapon = 'rifle' | 'pistol' | 'rocket' | 'gl40';   // (S7: the rocket tube, a LAW — built only for someone who carries one; 65.31: the 40 mm, likewise)
/**
 * 65.12: the long gun a person carries in the rifle's place — shouldered in the hand, slung on the back, reloaded the
 * same way. The rifle today; the SMG (compact, the close-range gun) and the LMG (a bipod and a box magazine) are
 * models only until 65.13 / 65.14 integrate them.
 */
export type LongGun = 'rifle' | 'smg' | 'lmg';

export interface WeaponModel {
  group: THREE.Group;
  muzzle: THREE.Object3D;
  gripR: THREE.Object3D;
  gripL: THREE.Object3D;
  /** the magazine, in the weapon's space; `well` is its seated position */
  magazine: THREE.Group;
  well: THREE.Vector3;
  /** 65.12, a long gun's fit to the body: the butt plate this far behind the origin (seated in the shoulder), and the support hand's slide along the handguard (`gripAt` forward, back to `gripMin`). Absent = the rifle's. */
  butt?: number;
  gripAt?: number;
  gripMin?: number;
  /** 65.12: the LMG's bipod legs, each hinged at the handguard (rotation.x: 0 folded along the barrel, `BIPOD_DOWN` deployed) */
  bipod?: THREE.Group[];
  /** 65.36: its magazine is a clip loaded from above (the Garand's en-bloc, the Springfield's stripper): the reload lifts it up and out, and presses the next down */
  clipFromTop?: boolean;
}

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
function box(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.position.set(x, y, z);
  parent.add(m);
  return m;
}
const at = (x: number, y: number, z: number): THREE.Object3D => { const o = new THREE.Object3D(); o.position.set(x, y, z); return o; };

/** The rifle (decision 3: direct fire first): today's handheld, with a magazine ahead of the pistol grip. */
/** The rifle's butt plate is this far behind its origin: seated in the shoulder pocket, the pivot sits there (personModel HOLD_AT.rifle). */
export const RIFLE_BUTT = 0.33;
/** The support hand's grip on the handguard: at the foregrip, sliding back as far as the magazine well when the foregrip is out of reach. */
export const RIFLE_GRIP_L = 0.22, RIFLE_GRIP_MIN = 0.16;

/**
 * The gun's two tones (owner, Oct 10: "an additional pass on the gun"): the metal (receiver, barrel, sights) and the
 * furniture (stock, grips, handguard) a lighter polymer, so the silhouette reads in parts — what you hold, what
 * fires — instead of one black bar.
 */
const FURNITURE = '#4b4a42';

export function buildRifle(p: UnitPalette): WeaponModel {
  const g = new THREE.Group();
  box(g, 0.08, 0.11, 0.34, p.metal, 0, 0, -0.01);          // receiver
  box(g, 0.075, 0.025, 0.3, p.metal, 0, 0.065, 0.0);       // the top rail along it
  box(g, 0.09, 0.1, 0.3, FURNITURE, 0, -0.005, 0.3);       // handguard round the barrel's back half
  box(g, 0.03, 0.02, 0.24, FURNITURE, 0.046, 0.0, 0.3);    // its side vent, a lighter line
  box(g, 0.045, 0.045, 0.3, p.metal, 0, 0.01, 0.6);        // barrel
  box(g, 0.065, 0.065, 0.06, p.metal, 0, 0.01, 0.765);     // muzzle brake (the shot leaves its tip)
  box(g, 0.06, 0.05, 0.12, p.dark, 0, 0.11, -0.02);        // the optic on the rail
  box(g, 0.05, 0.04, 0.03, p.flash, 0, 0.11, 0.045);       //   its lens, catching the team's light
  box(g, 0.03, 0.05, 0.03, p.metal, 0, 0.065, 0.42);       // front sight post
  box(g, 0.07, 0.12, 0.2, FURNITURE, 0, -0.03, -0.24);     // stock
  box(g, 0.06, 0.035, 0.14, FURNITURE, 0, 0.045, -0.24);   //   its cheek riser
  box(g, 0.075, 0.14, 0.025, p.dark, 0, -0.03, -RIFLE_BUTT);   // the butt plate, seated in the shoulder
  const grip = box(g, 0.055, 0.15, 0.065, FURNITURE, 0, -0.115, 0.01);   // pistol grip, raked back
  grip.rotation.x = 0.25;
  box(g, 0.05, 0.02, 0.09, p.metal, 0, -0.07, 0.07);       // trigger guard
  box(g, 0.055, 0.1, 0.065, FURNITURE, 0, -0.09, 0.24);    // foregrip
  box(g, 0.092, 0.035, 0.09, p.flash, 0, 0.0, 0.38);       // a team band on the handguard (reads at range)
  // the magazine: a curved-looking stack ahead of the grip, seated in the well under the receiver
  const magazine = new THREE.Group();
  box(magazine, 0.05, 0.1, 0.07, p.dark, 0, -0.05, 0);
  const curve = box(magazine, 0.05, 0.08, 0.07, p.dark, 0, -0.125, 0.018);   // its curve, forward toward the base
  curve.rotation.x = -0.3;
  box(magazine, 0.052, 0.025, 0.074, p.dark, 0, -0.165, 0.03);   // the base plate (one material: the magazine bakes to one mesh)
  const well = new THREE.Vector3(0, -0.055, 0.14);
  magazine.position.copy(well);
  g.add(magazine);
  g.add(at(0, 0.01, 0.79));                                  // muzzle
  const muzzle = g.children[g.children.length - 1];
  const gripR = at(0, -0.16, 0.02), gripL = at(0, -0.12, RIFLE_GRIP_L);   // the support hand on the handguard (it slides back when the foregrip is out of reach)
  g.add(gripR, gripL);
  return { group: g, muzzle, gripR, gripL, magazine, well };
}

/** The pistol: the sidearm, quick to draw (65.3). A slide over a frame, the grip raked back, the magazine in the grip. */
export function buildPistol(p: UnitPalette): WeaponModel {
  const g = new THREE.Group();
  box(g, 0.05, 0.05, 0.26, p.metal, 0, 0.02, 0.06);        // slide
  box(g, 0.046, 0.03, 0.2, FURNITURE, 0, -0.015, 0.05);    // frame (polymer, the lighter tone)
  box(g, 0.012, 0.018, 0.012, p.metal, 0, 0.053, 0.17);   // front sight
  box(g, 0.03, 0.016, 0.012, p.metal, 0, 0.053, -0.055);  // rear sight
  box(g, 0.05, 0.022, 0.05, p.flash, 0, 0.052, 0.0);        // a team flash on the slide's top
  box(g, 0.024, 0.024, 0.05, p.metal, 0, 0.0, 0.17);       // the muzzle end under the slide
  const grip = box(g, 0.044, 0.13, 0.055, FURNITURE, 0, -0.08, -0.04);
  grip.rotation.x = 0.28;                                   // raked back
  box(g, 0.05, 0.02, 0.07, p.dark, 0, -0.012, 0.02);       // the trigger guard
  const magazine = new THREE.Group();
  box(magazine, 0.034, 0.12, 0.04, p.dark, 0, -0.06, 0);
  box(magazine, 0.04, 0.016, 0.05, p.dark, 0, -0.125, 0.004);     // the base plate (one material: one mesh)
  const well = new THREE.Vector3(0, -0.07, -0.055);
  magazine.position.copy(well);
  magazine.rotation.x = 0.28;
  g.add(magazine);
  const muzzle = at(0, 0.02, 0.2); g.add(muzzle);
  const gripR = at(0, -0.11, -0.055), gripL = at(-0.03, -0.13, -0.03);   // the support hand wraps the grip's heel
  g.add(gripR, gripL);
  return { group: g, muzzle, gripR, gripL, magazine, well };
}

/**
 * 65.12 — the SMG (owner, Oct 10: the close-range gun, under about 30 people): compact — a short receiver, a stub
 * barrel in a vented shroud, a straight stick magazine, a wire stock (two rails and a pad), a small reflex sight and
 * a vertical foregrip. Two-thirds of the rifle's length, so it reads short beside it at Ground.
 */
export const SMG_FIT = { butt: 0.27, gripAt: 0.15, gripMin: 0.1 };
export function buildSmg(p: UnitPalette): WeaponModel {
  const g = new THREE.Group();
  box(g, 0.075, 0.1, 0.27, p.metal, 0, 0, 0.02);            // receiver
  box(g, 0.07, 0.022, 0.2, p.metal, 0, 0.06, 0.03);         // the top rail
  box(g, 0.07, 0.075, 0.16, FURNITURE, 0, 0.0, 0.22);       // the barrel shroud
  for (const z of [0.18, 0.22, 0.26]) box(g, 0.074, 0.012, 0.02, p.dark, 0, 0.02, z);   // its vents, three dark slots
  box(g, 0.04, 0.04, 0.08, p.metal, 0, 0.01, 0.33);         // the stub barrel past the shroud
  box(g, 0.05, 0.05, 0.035, p.metal, 0, 0.01, 0.38);        // the muzzle nut
  box(g, 0.05, 0.045, 0.07, p.dark, 0, 0.095, 0.03);        // the reflex sight
  box(g, 0.04, 0.035, 0.012, p.flash, 0, 0.1, 0.07);        //   its window, the team's light
  // the wire stock: two rails back to a pad (open, so it reads as folding, not as a solid block)
  for (const sx of [-0.025, 0.025]) box(g, 0.012, 0.012, 0.18, p.metal, sx, 0.0, -0.18);
  box(g, 0.065, 0.11, 0.025, p.dark, 0, -0.02, -SMG_FIT.butt);   // the pad, seated in the shoulder
  const grip = box(g, 0.05, 0.13, 0.06, FURNITURE, 0, -0.105, -0.03);   // pistol grip
  grip.rotation.x = 0.25;
  box(g, 0.045, 0.018, 0.08, p.metal, 0, -0.065, 0.02);     // trigger guard
  box(g, 0.045, 0.09, 0.05, FURNITURE, 0, -0.08, SMG_FIT.gripAt + 0.02);   // the vertical foregrip
  box(g, 0.077, 0.03, 0.06, p.flash, 0, 0.0, 0.27);         // a team band on the shroud
  // the magazine: a straight stick ahead of the grip
  const magazine = new THREE.Group();
  box(magazine, 0.045, 0.2, 0.06, p.dark, 0, -0.1, 0);
  box(magazine, 0.05, 0.02, 0.066, p.dark, 0, -0.205, 0);
  const well = new THREE.Vector3(0, -0.045, 0.1);
  magazine.position.copy(well);
  g.add(magazine);
  const muzzle = at(0, 0.01, 0.4); g.add(muzzle);
  const gripR = at(0, -0.15, -0.02), gripL = at(0, -0.12, SMG_FIT.gripAt);
  g.add(gripR, gripL);
  return { group: g, muzzle, gripR, gripL, magazine, well, ...SMG_FIT };
}

/**
 * 65.12 — the LMG (owner, Oct 10: a bipod and a box magazine; heavy, it slows its carrier): longer and thicker than
 * the rifle — a deep receiver with a carry handle, a heavy finned barrel, a long handguard with the bipod folded
 * under it, a box magazine hanging below the receiver, a solid stock. The bipod's legs fold forward along the barrel
 * and swing down to stand it (`setBipod`); prone, the person model puts them down.
 */
export const LMG_FIT = { butt: 0.37, gripAt: 0.3, gripMin: 0.13 };
/** The bipod's legs deployed: swung down from folded along the barrel to stand a little forward of plumb (radians). */
export const BIPOD_DOWN = 1.42;
export function buildLmg(p: UnitPalette): WeaponModel {
  const g = new THREE.Group();
  box(g, 0.1, 0.14, 0.44, p.metal, 0, 0.0, 0.0);            // receiver, deep
  box(g, 0.09, 0.03, 0.36, p.metal, 0, 0.085, 0.02);        // the top cover / rail
  box(g, 0.03, 0.06, 0.03, p.metal, 0, 0.12, 0.02);         // the carry handle's post
  box(g, 0.035, 0.025, 0.18, FURNITURE, 0, 0.16, 0.02);     //   and its grip
  box(g, 0.1, 0.11, 0.36, FURNITURE, 0, -0.005, 0.38);      // the long handguard
  box(g, 0.035, 0.022, 0.3, p.dark, 0.051, 0.0, 0.38);      // its side vent
  box(g, 0.06, 0.06, 0.36, p.metal, 0, 0.01, 0.74);         // the heavy barrel
  for (const z of [0.62, 0.68, 0.74]) box(g, 0.075, 0.075, 0.015, p.metal, 0, 0.01, z);   // cooling fins
  box(g, 0.08, 0.08, 0.07, p.metal, 0, 0.01, 0.94);         // the flash hider (the shot leaves its tip)
  box(g, 0.065, 0.055, 0.13, p.dark, 0, 0.13, -0.08);       // the optic behind the handle
  box(g, 0.055, 0.045, 0.03, p.flash, 0, 0.13, -0.01);      //   its lens
  box(g, 0.08, 0.14, 0.24, FURNITURE, 0, -0.03, -0.25);     // the solid stock
  box(g, 0.085, 0.16, 0.03, p.dark, 0, -0.03, -LMG_FIT.butt);   // the butt plate, seated in the shoulder
  const grip = box(g, 0.06, 0.15, 0.07, FURNITURE, 0, -0.13, -0.06);   // pistol grip
  grip.rotation.x = 0.25;
  box(g, 0.055, 0.02, 0.1, p.metal, 0, -0.085, 0.0);        // trigger guard
  box(g, 0.102, 0.04, 0.1, p.flash, 0, 0.0, 0.5);           // a team band on the handguard
  // the box magazine: squat, wide, hanging under the receiver ahead of the grip
  const magazine = new THREE.Group();
  box(magazine, 0.13, 0.15, 0.14, p.dark, 0, -0.075, 0);
  box(magazine, 0.135, 0.025, 0.145, p.dark, 0, -0.16, 0);
  const well = new THREE.Vector3(0, -0.07, 0.11);
  magazine.position.copy(well);
  g.add(magazine);
  // the bipod: two legs hinged under the handguard's front, folded forward along the barrel
  const bipod: THREE.Group[] = [];
  for (const sx of [-1, 1]) {
    const hinge = new THREE.Group();
    hinge.position.set(sx * 0.035, -0.06, 0.55);
    hinge.rotation.z = sx * 0.12;                             // splayed a little outward
    box(hinge, 0.022, 0.022, 0.3, p.metal, 0, 0, 0.15);       // the leg, along +z when folded
    box(hinge, 0.04, 0.02, 0.04, p.dark, 0, 0, 0.3);          // its foot
    g.add(hinge); bipod.push(hinge);
  }
  const muzzle = at(0, 0.01, 0.98); g.add(muzzle);
  const gripR = at(0, -0.18, -0.04), gripL = at(0, -0.12, LMG_FIT.gripAt);
  g.add(gripR, gripL);
  return { group: g, muzzle, gripR, gripL, magazine, well, bipod, ...LMG_FIT };
}

/** 65.12: the bipod's legs, `k` 0 folded … 1 down. */
export function setBipod(w: WeaponModel, k: number): void {
  for (const leg of w.bipod ?? []) leg.rotation.x = BIPOD_DOWN * Math.max(0, Math.min(1, k));
}

export function buildLongGun(kind: LongGun, p: UnitPalette): WeaponModel {
  return kind === 'smg' ? buildSmg(p) : kind === 'lmg' ? buildLmg(p) : buildRifle(p);
}

export function buildWeapon(kind: GroundWeapon, p: UnitPalette): WeaponModel {
  return kind === 'rifle' ? buildRifle(p) : kind === 'rocket' ? buildLaw(p) : kind === 'gl40' ? buildGl40(p) : buildPistol(p);
}

/**
 * S7, the rocket tube's look (the owner, Oct 11: "Rocket tube: that's actually a pretty good part of the game. Should
 * generate a model that looks like laws with a throwaway tube"): a LAW — a light, disposable, telescoping launcher. An
 * olive outer tube with its trigger housing on top, a yellow band near the front; collapsed it is short and capped at
 * both ends (carried strapped to the pack); extended, the inner tube slides out of the rear, the covers fall open and the
 * front and rear sights spring up (`setLawExtended`). Fired once, it is thrown away: `buildSpentLaw` is the tube left on
 * the ground. Built along +Z, the front (the muzzle) ahead, its origin where it rests on the shoulder. Sizes after the
 * M72, in person units (a person is 1.8): `LAW`.
 */
export const LAW = { length: 0.99, slide: 0.36, radius: 0.068, inner: 0.058, color: '#59603c', band: '#c8a83a', cap: '#23251c' };   // (the owner on #995: "diameter of tube should be 2x'd and length should be 1.5x'd")
/** The LAW's small parts scale with the tube: `W` across it (the diameter's ×2), `Z` along it (the length's ×1.5). */
const LW = 2, LZ = 1.5;
export interface LawModel extends WeaponModel {
  /** the inner tube (slides out of the rear), the sights (spring up), the covers (fall open): what `setLawExtended` moves */
  inner: THREE.Group; frontSight: THREE.Group; rearSight: THREE.Group; frontCover: THREE.Group; rearCover: THREE.Group;
  /** 65.49: other parts that fold flat as it is carried and stand up with the sights (the anti-dropship missile's antenna), as the front sight does */
  folds?: THREE.Group[];
}
function lawTube(g: THREE.Group, p: UnitPalette): { inner: THREE.Group } {
  const L = LAW, half = L.length / 2;
  const outer = new THREE.Mesh(new THREE.CylinderGeometry(L.radius, L.radius, L.length, 12), mat(L.color));
  outer.rotation.x = Math.PI / 2; g.add(outer);
  // the yellow band near the front, the trigger housing on top (its bar the right hand presses), the sling's two loops
  const band = new THREE.Mesh(new THREE.CylinderGeometry(L.radius + 0.003, L.radius + 0.003, 0.03 * LZ, 12), mat(L.band));
  band.rotation.x = Math.PI / 2; band.position.z = half - 0.07 * LZ; g.add(band);
  box(g, 0.03 * LW, 0.035 * LW, 0.11 * LZ, L.color, 0, L.radius + 0.012 * LW, 0.08 * LZ);
  box(g, 0.012 * LW, 0.012 * LW, 0.05 * LZ, p.dark, 0, L.radius + 0.034 * LW, 0.1 * LZ);   // the trigger bar
  box(g, 0.05 * LW, 0.012 * LW, 0.016 * LZ, p.dark, 0, -L.radius - 0.004 * LW, half - 0.06 * LZ);
  box(g, 0.05 * LW, 0.012 * LW, 0.016 * LZ, p.dark, 0, -L.radius - 0.004 * LW, -half + 0.08 * LZ);
  // the inner tube, slid in (z 0) or out of the rear (z −slide)
  const inner = new THREE.Group(); g.add(inner);
  const it = new THREE.Mesh(new THREE.CylinderGeometry(L.inner, L.inner, L.length * 0.9, 12), mat(FURNITURE));
  it.rotation.x = Math.PI / 2; it.position.z = -L.length * 0.05; inner.add(it);
  return { inner };
}
/** The LAW (S7): collapsed (`setLawExtended(law, 0)`) as built. */
export function buildLaw(p: UnitPalette): LawModel {
  const g = new THREE.Group(), L = LAW, half = L.length / 2;
  const { inner } = lawTube(g, p);
  // the covers: hinged at the tube's ends (a lid each), closed across the ends while it is carried
  const frontCover = new THREE.Group(); frontCover.position.set(0, L.radius, half); g.add(frontCover);
  const fc = new THREE.Mesh(new THREE.CylinderGeometry(L.radius + 0.004, L.radius + 0.004, 0.018, 12), mat(L.cap)); fc.position.set(0, -L.radius, 0.009); fc.rotation.x = Math.PI / 2; frontCover.add(fc);
  const rearCover = new THREE.Group(); rearCover.position.set(0, -L.radius, -half); inner.add(rearCover);
  const rc = new THREE.Mesh(new THREE.CylinderGeometry(L.radius + 0.004, L.radius + 0.004, 0.018, 12), mat(L.cap)); rc.position.set(0, L.radius, -0.009); rc.rotation.x = Math.PI / 2; rearCover.add(rc);
  // the sights: a frame at the front, a peep at the rear (on the inner tube: it rides out with it), folded flat until extended
  const frontSight = new THREE.Group(); frontSight.position.set(0, L.radius, half - 0.12 * LZ); g.add(frontSight);
  box(frontSight, 0.06, 0.006, 0.008, p.dark, 0, 0.06, 0); box(frontSight, 0.006, 0.06, 0.008, p.dark, -0.027, 0.03, 0); box(frontSight, 0.006, 0.06, 0.008, p.dark, 0.027, 0.03, 0);
  const rearSight = new THREE.Group(); rearSight.position.set(0, L.inner, -half + 0.05 * LZ); inner.add(rearSight);
  box(rearSight, 0.036, 0.05, 0.008, p.dark, 0, 0.033, 0);
  const muzzle = at(0, 0, half + 0.01); g.add(muzzle);
  const gripR = at(0.0, L.radius + 0.05, 0.15), gripL = at(-0.01, -L.radius - 0.03, half - 0.18);   // the right hand on the trigger bar, the left under the front
  g.add(gripR, gripL);
  const law: LawModel = { group: g, muzzle, gripR, gripL, magazine: inner, well: new THREE.Vector3(), inner, frontSight, rearSight, frontCover, rearCover };
  setLawExtended(law, 0);
  return law;
}
/** Extend the LAW `k` of the way (0 collapsed and capped, 1 ready to fire): the inner tube out of the rear, the covers open, the sights up. */
export function setLawExtended(law: LawModel, k: number): void {
  const e = Math.max(0, Math.min(1, k));
  law.inner.position.z = -LAW.slide * e;
  law.frontCover.rotation.x = -2.3 * Math.min(1, e * 2);   // falls open first
  law.rearCover.rotation.x = 2.3 * Math.min(1, e * 2);
  law.frontSight.rotation.x = -Math.PI / 2 * (1 - smoothstep(0.5, 1, e));   // folded flat (forward) until the tube is out, then springs up
  law.rearSight.rotation.x = Math.PI / 2 * (1 - smoothstep(0.5, 1, e));
  if (law.folds) for (const f of law.folds) f.rotation.x = law.frontSight.rotation.x;
}
/** S7: the spent LAW — the tube thrown away after its one shot, left extended, covers hanging open, sights down: a small debris piece. */
export function buildSpentLaw(p: UnitPalette): THREE.Group { return spentLaw(buildLaw(p)); }
/** A built tube made the spent one (65.36: any look's). */
export function spentLaw(law: LawModel): THREE.Group {
  setLawExtended(law, 1);
  law.frontSight.rotation.x = -Math.PI / 2; law.rearSight.rotation.x = Math.PI / 2;   // knocked flat
  for (const o of [law.gripR, law.gripL, law.muzzle]) o.removeFromParent();
  return law.group;
}
const smoothstep = (a: number, b: number, x: number) => { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/**
 * 65.31, the 40 mm's look (the owner: "vietnam era breakbarrel 40mm"): an M79-class break-barrel launcher — a short, fat
 * barrel on a hinge ahead of the receiver, a wooden stock and fore-end (the fore-end rides the barrel), a folding leaf sight
 * on the barrel's top, a rubber butt pad, the latch on the receiver's top. The break action opens for the reload: the barrel
 * tips down about its hinge (`setGl40Open`), the empty case comes out and a fresh round goes in — the round is the
 * "magazine" the reload animation carries (its `well` the open chamber's mouth). The leaf stands up while it is aimed
 * (`setGl40Leaf`). Built along +Z, its origin at the receiver; shouldered like the rifle (`butt`), the support hand under
 * the fore-end. Sizes after the M79 (0.73 long, a 40 mm bore), in person units (a person is 1.8).
 */
export const GL40 = { butt: 0.36, gripAt: 0.2, gripMin: 0.13, open: 0.62, wood: '#7a5232', woodDark: '#5c3d24', brass: '#b8903c', round: '#4f5a3a', hinge: { y: -0.032, z: 0.07 } };
export interface Gl40Model extends WeaponModel {
  /** the barrel and its fore-end and leaf, hinged ahead of the receiver (rotation.x: 0 closed, GL40.open broken open) */
  barrel: THREE.Group;
  /** the leaf sight's frame, hinged on the barrel's top (rotation.x: −π/2 folded flat forward, 0 up) */
  leaf: THREE.Group;
}
function cyl(parent: THREE.Object3D, r: number, len: number, color: string, x: number, y: number, z: number, seg = 10): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, len, seg), mat(color));
  m.rotation.x = Math.PI / 2; m.position.set(x, y, z);
  parent.add(m);
  return m;
}
export function buildGl40(p: UnitPalette): Gl40Model {
  const g = new THREE.Group(), W = GL40.wood;
  // the receiver: a short block, the latch lever on its top, the trigger guard under it
  box(g, 0.064, 0.085, 0.15, p.metal, 0, -0.004, 0.0);
  box(g, 0.022, 0.016, 0.06, p.dark, 0, 0.046, 0.0);        // the top latch (thumbed aside to break it)
  box(g, 0.04, 0.016, 0.08, p.metal, 0, -0.066, 0.005);     // the trigger guard
  box(g, 0.066, 0.02, 0.05, p.flash, 0, 0.0, -0.05);        // a team flash on the receiver's side
  // the stock: a narrow wrist into the wooden butt, dropped a little, the rubber pad at its end
  const wrist = box(g, 0.046, 0.07, 0.1, W, 0, -0.02, -0.12); wrist.rotation.x = 0.08;
  const stock = box(g, 0.058, 0.12, 0.2, W, 0, -0.042, -0.25); stock.rotation.x = 0.06;
  box(g, 0.06, 0.13, 0.022, p.dark, 0, -0.05, -GL40.butt);  // the butt pad, seated in the shoulder
  box(g, 0.05, 0.02, 0.16, GL40.woodDark, 0, 0.022, -0.25); // the comb, a darker grain line along its top
  // the barrel: hinged under the breech ahead of the receiver; short and fat, the fore-end under it, the leaf on top
  const barrel = new THREE.Group(); barrel.position.set(0, GL40.hinge.y, GL40.hinge.z); g.add(barrel);
  const by = -GL40.hinge.y;                                  // the bore's axis, in the barrel's space
  cyl(barrel, 0.034, 0.36, p.metal, 0, by, 0.18, 12);       // the barrel, 40 mm bore and its wall
  cyl(barrel, 0.025, 0.012, p.dark, 0, by, 0.362, 12);      // the bore's dark mouth at the muzzle
  box(barrel, 0.062, 0.05, 0.19, W, 0, by - 0.042, 0.13);   // the wooden fore-end under it
  box(barrel, 0.012, 0.03, 0.012, p.metal, 0, by + 0.045, 0.33);   // the front sight blade
  const leaf = new THREE.Group(); leaf.position.set(0, by + 0.034, 0.05); barrel.add(leaf);
  box(leaf, 0.008, 0.075, 0.006, p.dark, -0.02, 0.0375, 0); box(leaf, 0.008, 0.075, 0.006, p.dark, 0.02, 0.0375, 0);   // the frame's two rails
  for (const y of [0.025, 0.05, 0.072]) box(leaf, 0.04, 0.005, 0.006, p.dark, 0, y, 0);   // its range bars (the ladder)
  box(leaf, 0.05, 0.012, 0.02, p.metal, 0, 0, 0);          // its hinge block on the barrel
  // the round (the reload's "magazine"): the brass case and the olive projectile head, along the bore
  const magazine = new THREE.Group();
  cyl(magazine, 0.021, 0.05, GL40.brass, 0, 0, -0.025, 10);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.021, 10, 6), mat(GL40.round)); head.scale.set(1, 1, 1.3); magazine.add(head);
  // its seat: the open chamber's mouth (the barrel broken GL40.open about its hinge), along the tipped bore
  const c = Math.cos(GL40.open), s = Math.sin(GL40.open);
  const well = new THREE.Vector3(0, GL40.hinge.y + by * c, GL40.hinge.z + by * s);
  magazine.position.copy(well); magazine.rotation.x = GL40.open;
  g.add(magazine);
  const muzzle = at(0, by, 0.37); barrel.add(muzzle);
  const gripR = at(0, -0.1, -0.06), gripL = at(0, -0.1, GL40.gripAt);   // the right hand on the wrist, the left under the fore-end
  g.add(gripR, gripL);
  const m: Gl40Model = { group: g, muzzle, gripR, gripL, magazine, well, butt: GL40.butt, gripAt: GL40.gripAt, gripMin: GL40.gripMin, barrel, leaf };
  setGl40Open(m, 0); setGl40Leaf(m, 0);
  return m;
}
/** 65.31: break the 40 mm open `k` of the way (0 closed, 1 the barrel tipped GL40.open down about its hinge). */
export function setGl40Open(m: Gl40Model, k: number): void { m.barrel.rotation.x = GL40.open * Math.max(0, Math.min(1, k)); }
/** 65.31: the leaf sight `k` of the way up (0 folded flat forward on the barrel, 1 standing). */
export function setGl40Leaf(m: Gl40Model, k: number): void { m.leaf.rotation.x = -Math.PI / 2 * (1 - Math.max(0, Math.min(1, k))); }

/** How long each weapon's reload and the draw take on screen (seconds) — the looks' own pacing; lane C's table sets the sim's. */
export const WEAPON_LOOK = {
  rifle: { reload: 2.2 },
  pistol: { reload: 1.5 },
  /** 65.12, the looks' pacing only (65.13 / 65.14 set the sim's): the SMG's stick is quick, the LMG's box slow */
  smg: { reload: 1.8 },
  lmg: { reload: 3.4 },
  /** S7: the LAW's "reload" is the throwaway — the spent tube dropped, the next drawn from the pack and extended (the sim's 3 s) */
  rocket: { reload: 3.0 },
  /** 65.31: the break-barrel's — break it, the case out, a round in, close it (the sim's 2.5 s) */
  gl40: { reload: 2.5 },
  /** the switch between them (the rifle to the sling, the pistol from the holster, or back) */
  draw: 0.45,
};
